import { ThemeProvider as MaterialThemeProvider } from '@mui/material';
import { amber, blue, cyan, green, grey, orange, red, yellow } from '@mui/material/colors';
import { Theme } from '@mui/material/styles';
import React, { createContext, PropsWithChildren, useMemo } from 'react';
import { useOnMobile, useOnTablet } from '../hooks/breakpoints';
import { useBrowserOrAccountLocale } from '../hooks/useBrowserOrAccountLocale';
import { getTheme, TIMELINE_COLORS_BY_EVENT_TYPE, TIMELINE_COLORS_BY_STATUS } from '../Theme';
import { useConfigDarkMode } from '../utils/atoms/useConfigDarkMode';

type StylingContextType = {
    theme: Theme;
    isDarkMode: boolean;
    setIsDarkMode: (isDark: boolean) => void;
    isMobile: boolean;
    isTablet: boolean;
    timelineColorsByStatus: Record<string, string>;
    aiExtractedFieldColor: Record<string, string>;
    timelineColorsByEventType: Record<string, string>;
    attributeColorsByResourceStatus: Record<string, string>;
    forceMode?: 'light' | 'dark';
};

type StylingProviderProps = {
    forceMode?: 'light' | 'dark';
};

export const StylingContext = createContext<StylingContextType | null>(null);

export const StylingProvider = ({ forceMode, children }: PropsWithChildren<StylingProviderProps>) => {
    const isMobile = useOnMobile();
    const isTablet = useOnTablet();
    const [isDarkMode, setIsDarkMode] = useConfigDarkMode();

    const locale = useBrowserOrAccountLocale();

    const resolvedDarkMode = typeof forceMode === 'string' ? forceMode === 'dark' : isDarkMode;

    const theme = useMemo(() => getTheme(resolvedDarkMode, locale), [resolvedDarkMode, locale]);

    const aiExtractedFieldColor = useMemo(() => {
        const fieldColor = {
            HIGHLIGHTED: amber[900],
            NORMAL: blue[700]
        };
        return resolvedDarkMode
            ? {
                  ...fieldColor,
                  DEFAULT: grey.A700
              }
            : {
                  ...fieldColor,
                  DEFAULT: grey[400]
              };
    }, [resolvedDarkMode]);

    const attributeColorsByResourceStatus = useMemo(() => {
        return resolvedDarkMode
            ? {
                  Open: grey['200'],
                  Consideration: cyan['200'],
                  'Request for Proposal': blue['200'],
                  'Proposal Review': orange['200'],
                  Negotiating: yellow['400'],
                  Booked: green['200'],
                  Closed: red['200']
              }
            : {
                  Open: grey['400'],
                  Consideration: cyan['400'],
                  'Request for Proposal': blue['400'],
                  'Proposal Review': orange['400'],
                  Negotiating: yellow['600'],
                  Booked: green['400'],
                  Closed: red['400']
              };
    }, [resolvedDarkMode]);

    const contextValue = useMemo(
        () => ({
            theme,
            forceMode,
            isDarkMode: resolvedDarkMode,
            setIsDarkMode,
            isMobile,
            isTablet,
            timelineColorsByStatus: TIMELINE_COLORS_BY_STATUS,
            aiExtractedFieldColor,
            timelineColorsByEventType: TIMELINE_COLORS_BY_EVENT_TYPE,
            attributeColorsByResourceStatus
        }),
        [
            theme,
            forceMode,
            resolvedDarkMode,
            setIsDarkMode,
            isMobile,
            isTablet,
            aiExtractedFieldColor,
            attributeColorsByResourceStatus
        ]
    );

    return (
        <StylingContext value={contextValue}>
            <MaterialThemeProvider theme={theme}>{children}</MaterialThemeProvider>
        </StylingContext>
    );
};
